import { useEffect, useRef } from 'react';
import { postMessage } from '../helpers/utils';
import {
  AUDIO_STOPPED_REPORT_DELAY_MS,
  MESSAGE_TYPES
} from '../constants/simulationConstants';
import { useSimulationContext } from '../providers/SimulationProvider';

/**
 * Reports when the coach starts and stops speaking. The parent app uses this to
 * show the audio/mute control beside the latest AI message.
 *
 * Mounted at container level, deliberately outside <Canvas>: react-three-fiber
 * only re-renders its tree while the canvas measures larger than 0x0, so a
 * reporter living inside the scene stops seeing isAiTalking change as soon as
 * the parent hides the avatar (display: none) — which is exactly when the
 * coach's voice keeps playing without the avatar.
 */
function useAudioPlaybackReporter() {
  const { isAiTalking } = useSimulationContext();
  const stoppedTimeoutRef = useRef(null);

  useEffect(() => {
    if (stoppedTimeoutRef.current) {
      clearTimeout(stoppedTimeoutRef.current);
      stoppedTimeoutRef.current = null;
    }

    // Starting is reported at once — the parent locks the chat on it.
    if (isAiTalking) {
      postMessage({ type: MESSAGE_TYPES.AUDIO, payload: { isPlaying: true } });
      return undefined;
    }

    // Stopping waits out a quiet window instead. The local queue reports a
    // silence between two chunks the same way it reports the end of a reply,
    // and releasing the chat on the first one would let the learner type into
    // the middle of the coach's answer.
    stoppedTimeoutRef.current = setTimeout(() => {
      stoppedTimeoutRef.current = null;
      postMessage({
        type: MESSAGE_TYPES.AUDIO,
        payload: { isPlaying: false, muted: false }
      });
    }, AUDIO_STOPPED_REPORT_DELAY_MS);

    return () => {
      if (!stoppedTimeoutRef.current) return;
      clearTimeout(stoppedTimeoutRef.current);
      stoppedTimeoutRef.current = null;
    };
  }, [isAiTalking]);
}

export default useAudioPlaybackReporter;
